import { AppSVG } from "./ImagePath";

/**
 * Central currency registry.
 *
 * Adding a currency is a single entry here — result popups, the marquee and the
 * confirmation modal all read from this map, so no other file needs to learn
 * about a new currency code.
 *
 * Fields:
 *   icon       image URL to render, or null to fall back to the `symbol` glyph
 *   symbol     text glyph shown when there is no icon (e.g. "$")
 *   minorUnits true when API amounts are in minor units (cents) and must be /100
 *              for display; false when the amount is already whole (gems/tickets).
 *   demo       true when amounts in this currency must never be shown to the player;
 *              render DEMO_AMOUNT_LABEL instead (see isAmountHidden). Balances
 *              still move server-side.
 *
 * Vocabulary note: `/lobby` and match data always speak DraftKings' USD/GEMS
 * vocabulary (backend enum). Pixel only ever appears as the *session* currency
 * (GEM/RCR); see resolveDisplayCurrency for the icon bridge, and the Pixel lobby
 * filter in GameLobby.jsx for the session→lobby currency map.
 */
export const CURRENCIES = {
  USD: { icon: null, symbol: "$", minorUnits: true },
  // DraftKings GEMS is backend-owned (no real money). With
  // REACT_APP_GEMS_DEMO_MODE=true, gem lobbies act as demo games and no gem
  // amount is shown. Set it on DraftKings builds only: Pixel lobby data also
  // says GEMS (shown as its real-money GEM), so the flag would hide those too.
  GEMS: {
    icon: AppSVG.gemIcon,
    symbol: "",
    minorUnits: false,
    demo: process.env.REACT_APP_GEMS_DEMO_MODE === "true",
  },
  RCR: { icon: AppSVG.rcrIcon, symbol: "", minorUnits: true },
  // Pixel GEM renders in minor units (÷100) like RCR/USD — 600 -> 6.00. This is
  // Pixel-only: DraftKings' GEMS above stays whole (resolveDisplayCurrency only
  // maps GEMS->GEM on the Pixel build).
  GEM: { icon: AppSVG.gemIconPixel, symbol: "", minorUnits: true },
};

const DEFAULT_CODE = "USD";

export function currencyConfig(code) {
  return CURRENCIES[code] || CURRENCIES[DEFAULT_CODE];
}

/**
 * True when amounts (balance, entry fee, prize, winnings) in this currency must
 * not be rendered. Pass the *data* code from lobby/match data (USD/GEMS), not the
 * resolved display code.
 */
export function isAmountHidden(dataCode) {
  return Boolean(currencyConfig(dataCode).demo);
}

/**
 * Text shown in place of an amount when isAmountHidden is true (no currency icon):
 * entry fees -> FREE, everything else (prize pool, winnings) -> "-".
 */
export const DEMO_AMOUNT_LABEL = { entry: "FREE", amount: "-" };

/**
 * Per-client map from DraftKings' lobby/match currency (USD/GEMS) to the currency
 * this client displays. DraftKings is the identity mapping (omitted). Single source
 * of the lobby↔session currency relationship: the Pixel lobby filter reuses
 * resolveDisplayCurrency rather than a second (inverse) map.
 */
const DISPLAY_CURRENCY_BY_CLIENT = {
  pixel: { USD: "RCR", GEMS: "GEM" },
};

/**
 * The currency code to render icons/amounts for. Lobby & match data always speak
 * DraftKings' USD/GEMS vocabulary; map each into the active client's currency so a
 * Pixel build shows RCR / GEM. For DraftKings (or an unset client) the data code
 * passes through unchanged.
 */
export function resolveDisplayCurrency(dataCode) {
  const map = DISPLAY_CURRENCY_BY_CLIENT[process.env.REACT_APP_CLIENT];
  return map?.[dataCode] ?? dataCode;
}

/** Divide minor units (cents) to major when required; whole currencies pass through. */
export function formatCurrencyAmount(amount, code) {
  const value = Number(amount) || 0;
  return currencyConfig(code).minorUnits ? (value / 100).toFixed(2) : value;
}
